/* grove-header.js — The Grove Line custom header behaviour.
   Vanilla web components, namespaced under window.GroveLine. No inline handlers. */
(function () {
  'use strict';

  window.GroveLine = window.GroveLine || {};

  /* The menu panels and the search panel each lock body scroll while open, and
     one can hand over to the other (menu ⌕ → search). dialog.close() fires its
     close event in a later task, so the panel being left tears down after the
     panel being entered has set up — hence the check for any dialog still open
     rather than each component trusting its own. */
  function releaseScrollLock() {
    if (document.querySelector('dialog[open]')) return;
    document.body.classList.remove('overflow-hidden-tablet');
  }

  /* Safari restores a page from the back-forward cache with the DOM exactly as
     it was left — including a menu panel that was still open when the visitor
     tapped a link inside it, and the body scroll lock that came with it. WebKit
     does not restore top-layer membership, so that panel repaints as an opaque
     fixed sheet over the page with scrolling dead: it reads as "the page did
     not load" until a reload. Nothing else re-runs on a bfcache restore
     (DOMContentLoaded does not fire), so unwind every dialog and lock here. */
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('dialog[open]').forEach((panel) => panel.close());
    document.body.classList.remove('overflow-hidden', 'overflow-hidden-tablet', 'overflow-hidden-mobile');
  });

  /* <grove-search> — inline expanding search field in the header bar.
     Toggle opens/closes the field, focuses the input, and closes on Escape,
     submit, or a click/focus outside the component. */
  class GroveSearch extends HTMLElement {
    constructor() {
      super();
      this.toggle = this.querySelector('.grove-search__toggle');
      this.input = this.querySelector('.grove-search__input');
      this.onToggle = this.onToggle.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocPointer = this.onDocPointer.bind(this);
    }

    connectedCallback() {
      if (!this.toggle || !this.input) return;
      this.toggle.addEventListener('click', this.onToggle);
      this.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onDocPointer);
    }

    get open() {
      return this.classList.contains('grove-search--open');
    }

    onToggle() {
      this.open ? this.close() : this.openSearch();
    }

    openSearch() {
      this.classList.add('grove-search--open');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.input.removeAttribute('tabindex');
      this.input.focus();
      document.addEventListener('pointerdown', this.onDocPointer);
    }

    close() {
      this.classList.remove('grove-search--open');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('tabindex', '-1');
      document.removeEventListener('pointerdown', this.onDocPointer);
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.open) {
        this.close();
        this.toggle.focus();
      }
    }

    onDocPointer(event) {
      if (!this.contains(event.target)) this.close();
    }
  }

  if (!customElements.get('grove-search')) {
    customElements.define('grove-search', GroveSearch);
  }

  /* <grove-search-panel> — mobile search: full-screen <dialog> (snippet:
     grove-search-panel). Same mechanics as the menu panels: showModal() for a
     native focus trap, Esc and focus restoration; the shared chrome row means
     CLOSE and BAG are handled here too, since those data hooks are only
     intercepted by whichever component owns the panel. */
  class GroveSearchPanel extends HTMLElement {
    connectedCallback() {
      this.openBtn = this.querySelector('[data-panel-open]');
      this.panel = this.querySelector('[data-search-panel]');
      this.input = this.querySelector('input[type="search"]');
      if (!this.openBtn || !this.panel) return;

      this.openBtn.addEventListener('click', () => this.open());
      this.panel.addEventListener('close', () => this.onClose());
      this.addEventListener('click', this.onClick.bind(this));
      // Escape inside an <input type="search"> is consumed by the browser to
      // clear the field, so the dialog's native cancel never fires while the
      // field has focus — close the panel on the first press either way.
      this.panel.addEventListener('keydown', this.onKeydown.bind(this));
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.panel.close();
    }

    open() {
      if (this.panel.open) return;
      this.panel.showModal();
      document.body.classList.add('overflow-hidden-tablet');
      this.openBtn.setAttribute('aria-expanded', 'true');
      // Autofocus would land on the first tabbable element (CLOSE) instead.
      this.input?.focus();
    }

    onClick(event) {
      if (event.target.closest('[data-menu-close]')) {
        this.panel.close();
        return;
      }

      const bag = event.target.closest('[data-menu-bag]');
      if (bag && document.getElementById('cart-icon-bubble')) {
        // Cart drawer would render beneath the dialog top layer — close the
        // panel first, then delegate to the real header BAG link.
        event.preventDefault();
        this.panel.close();
        document.getElementById('cart-icon-bubble').click();
      }
    }

    onClose() {
      this.openBtn.setAttribute('aria-expanded', 'false');
      releaseScrollLock();
    }
  }

  if (!customElements.get('grove-search-panel')) {
    customElements.define('grove-search-panel', GroveSearchPanel);
  }

  /* <grove-mobile-menu> — stacked drill-down menu panels (mobile).
     Panels are pre-rendered <dialog>s (snippet: grove-mobile-drawer) opened
     with showModal(), so focus trap, top-layer stacking and focus restoration
     are native. BACK closes one panel (revealing the parent beneath), CLOSE
     unwinds the whole stack, Esc pops the topmost panel natively. The root
     panel opens instantly — the burger icon morphing to/from an X (openRoot /
     onPanelClose) carries that transition, and it and the panels' pre-morphed
     X share one optical spot. Subpanels slide in from the right; that motion
     is pure CSS (grove-header.css), so there is no timing to sync here. */
  class GroveMobileMenu extends HTMLElement {
    connectedCallback() {
      this.openBtn = this.querySelector('[data-menu-open]');
      this.openIcon = this.openBtn ? this.openBtn.querySelector('.grove-burger') : null;
      this.panels = Array.from(this.querySelectorAll('[data-menu-panel]'));
      if (!this.openBtn || this.panels.length === 0) return;

      this.onClick = this.onClick.bind(this);
      this.onPanelClose = this.onPanelClose.bind(this);
      this.openBtn.addEventListener('click', () => this.openRoot());
      this.addEventListener('click', this.onClick);
      this.panels.forEach((panel) => panel.addEventListener('close', this.onPanelClose));
    }

    /* Morph the header burger into an X, then open the root panel — its
       chrome renders the same icon pre-morphed in the same spot, so the
       instant panel swap reads as the icon continuing to sit there. The
       reverse morph happens in onPanelClose once the header is revealed. */
    openRoot() {
      if (this.panels[0].open || this.morphTimer) return;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!this.openIcon || reducedMotion) {
        this.openPanel(this.panels[0]);
        return;
      }
      this.openIcon.classList.add('grove-burger--x');
      this.morphTimer = setTimeout(() => {
        this.morphTimer = null;
        this.openPanel(this.panels[0]);
      }, 200); // matches .grove-burger__bar transition (--grove-duration-fast in grove-tokens.liquid)
    }

    openPanel(panel) {
      if (!panel || panel.open) return;
      panel.showModal();
      document.body.classList.add('overflow-hidden-tablet');
      this.openBtn.setAttribute('aria-expanded', 'true');
    }

    onClick(event) {
      const trigger = event.target.closest('[data-submenu-trigger]');
      if (trigger) {
        this.openPanel(document.getElementById(trigger.dataset.target));
        return;
      }

      if (event.target.closest('[data-menu-back]')) {
        this.closePanel(event.target.closest('dialog'));
        return;
      }

      if (event.target.closest('[data-menu-close]')) {
        this.closeAll();
        return;
      }

      if (event.target.closest('[data-menu-search]')) {
        // The search panel belongs to <grove-search-panel> in the header bar —
        // unwind the menu, then let that component open its own dialog.
        const opener = document.querySelector('grove-search-panel [data-panel-open]');
        this.closeAll();
        if (opener) opener.click();
        return;
      }

      const bag = event.target.closest('[data-menu-bag]');
      if (bag && document.getElementById('cart-icon-bubble')) {
        // Cart drawer would render beneath the dialog top layer — close the
        // menu first, then delegate to the real header BAG link.
        event.preventDefault();
        this.closeAll();
        document.getElementById('cart-icon-bubble').click();
      }
    }

    closePanel(panel) {
      if (panel && panel.open) panel.close();
    }

    closeAll() {
      this.panels.filter((panel) => panel.open).reverse().forEach((panel) => panel.close());
    }

    onPanelClose() {
      if (this.panels.some((panel) => panel.open)) return;
      this.openBtn.setAttribute('aria-expanded', 'false');
      // Header bar just got revealed with the icon still in X state — remove
      // the class now so it morphs back into the burger.
      if (this.openIcon) this.openIcon.classList.remove('grove-burger--x');
      releaseScrollLock();
    }
  }

  if (!customElements.get('grove-mobile-menu')) {
    customElements.define('grove-mobile-menu', GroveMobileMenu);
  }

  /* "BAG (2)" count (snippets/grove-bag-count.liquid). After a cart change
     Dawn's cart JS re-renders only #cart-icon-bubble, so the other copies — the
     menu panel's BAG and the /cart title — would keep the page-load count.
     Watch the bubble and mirror its count into them. */
  function syncBagCount() {
    const bubble = document.getElementById('cart-icon-bubble');
    const source = bubble && bubble.querySelector('[data-grove-bag-count]');
    if (!source) return;
    document.querySelectorAll('[data-grove-bag-count]').forEach((count) => {
      if (count !== source && count.textContent !== source.textContent) {
        count.textContent = source.textContent;
      }
    });
  }

  function watchBagCount() {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    new MutationObserver(syncBagCount).observe(bubble, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchBagCount);
  } else {
    watchBagCount();
  }
})();
